// Vuex-to-Pinia compatibility layer
// Provides $store API that maps to Pinia stores

import { useAccountStore } from "./stores/account";
import { useTemplatesStore } from "./stores/admin/templates";
import { useTemplateCollectionsStore } from "./stores/admin/templateCollections";
import { useOrganisationsStore } from "./stores/admin/organisations";
import { useVesselsStore } from "./stores/admin/vessels";
import { useUsersStore } from "./stores/admin/users";
import { useFeaturesStore } from "./stores/admin/features";
import { useRolesStore } from "./stores/admin/roles";
import { useVesselCatalogItemStore } from "./stores/admin/vesselCatalogItem";
import { useCollectionsStore } from "./stores/collections";
import { useCategoriesStore } from "./stores/categories";
import { useChecklistsStore } from "./stores/checklists";
import { useChecklistRowsStore } from "./stores/checklistRows";
import { useCatalogStore } from "./stores/catalog";
import { useAnnouncementsStore } from "./stores/announcements";
import { useAnnouncementCategoriesStore } from "./stores/announcementCategories";
import { useImagesStore } from "./stores/images";
import { useAppLocalStore } from "./stores/appLocal";

export const VuexCompatibilityPlugin = {
  install(app) {
    // Lazy store getter - only create stores when needed
    const getStore = (storeName) => {
      try {
        switch (storeName) {
          case "account":
            return useAccountStore();
          case "templates":
            return useTemplatesStore();
          case "templateCollections":
            return useTemplateCollectionsStore();
          case "organisations":
            return useOrganisationsStore();
          case "vessels":
            return useVesselsStore();
          case "users":
            return useUsersStore();
          case "features":
            return useFeaturesStore();
          case "roles":
            return useRolesStore();
          case "vesselCatalogItem":
            return useVesselCatalogItemStore();
          case "collections":
            return useCollectionsStore();
          case "categories":
            return useCategoriesStore();
          case "checklists":
            return useChecklistsStore();
          case "checklistRows":
            return useChecklistRowsStore();
          case "catalog":
            return useCatalogStore();
          case "announcements":
            return useAnnouncementsStore();
          case "announcementCategories":
            return useAnnouncementCategoriesStore();
          case "images":
            return useImagesStore();
          case "appLocal": {
            const appLocalStore = useAppLocalStore();
            return appLocalStore;
          }
          default:
            console.warn(`Unknown store: ${storeName}`);
            return null;
        }
      } catch (error) {
        console.warn(`Error accessing store "${storeName}":`, error);
        return null;
      }
    };

    const ensureArray = (value) => (Array.isArray(value) ? value : []);
    const ensureValue = (value, defaultValue = null) =>
      value !== undefined && value !== null ? value : defaultValue;

    // Create Vuex-like $store object
    const $store = {
      getters: new Proxy(
        {},
        {
          get(target, prop) {
            const [storeName, getterName] = prop.split("/");
            const store = getStore(storeName);

            if (!store) {
              console.warn(`Store "${storeName}" not found or not ready`);
              return undefined;
            }

            // Handle special mapping cases
            const mappingKey = `${storeName}/${getterName}`;

            const getterMappings = {
              // Catalog store mappings
              "catalog/catalog": ensureArray(store.catalogList),
              "catalog/catalogItemById": (id) =>
                ensureValue(store.catalogItemById(id)),
              "catalog/catalogItemByTargetId": (targetId) =>
                ensureValue(store.catalogItemByTargetId(targetId)),
              "catalog/pdfDownloadUrl": ensureValue(store.pdfDownloadUrl),

              // Collections store mappings
              "collections/collections": ensureArray(store.collectionsList),
              "collections/collectionById": (id) =>
                ensureValue(store.collectionById(id)),

              // Categories store mappings
              "categories/categories": ensureArray(store.categoriesList),
              "categories/categoryById": (id) =>
                ensureValue(store.categoryById(id)),
              "categories/editorChanges": ensureValue(store.editorChanges, {}),
              "categories/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // Checklists store mappings
              "checklists/checklists": ensureArray(store.checklistsList),
              "checklists/checklistById": (id) =>
                ensureValue(store.checklistById(id)),
              "checklists/editorChanges": ensureValue(store.editorChanges, {}),
              "checklists/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // ChecklistRows store mappings
              "checklistRows/checklistRows": ensureArray(
                store.checklistRowsList,
              ),
              "checklistRows/rowsByChecklist": (checklistId) =>
                ensureArray(store.rowsByChecklist(checklistId)),
              "checklistRows/checklistRowById": (id) =>
                ensureValue(store.checklistRowById(id)),
              "checklistRows/editorChanges": ensureValue(
                store.editorChanges,
                {},
              ),
              "checklistRows/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // Account store mappings
              "account/info": ensureValue(store.info, {}),
              "account/checklistsFeatureEnabled": ensureValue(
                store.checklistsFeatureEnabled,
                false,
              ),
              "account/announcementsFeatureEnabled": ensureValue(
                store.announcementsFeatureEnabled,
                false,
              ),

              // Announcements store mappings
              "announcements/announcements": ensureArray(
                store.announcementsList,
              ),
              "announcements/announcementsByCategoryId": (categoryId) =>
                ensureArray(store.announcementsByCategoryId(categoryId)),
              "announcements/announcementApiError": ensureValue(
                store.announcementApiError,
              ),
              "announcements/editorChanges": ensureValue(
                store.editorChanges,
                {},
              ),
              "announcements/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // AnnouncementCategories store mappings
              "announcementCategories/announcementCategories": ensureArray(
                store.announcementCategoriesList,
              ),
              "announcementCategories/announcementCategoryById": (id) =>
                ensureValue(store.announcementCategoryById(id)),
              "announcementCategories/editorChanges": ensureValue(
                store.editorChanges,
                {},
              ),
              "announcementCategories/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // Images store mappings
              "images/imageUrlByName": (name) => store.imageUrlByName(name),

              // AppLocal store mappings
              "appLocal/announcement_list_selected_collection_ids": ensureArray(
                store.announcement_list_selected_collection_ids,
              ),

              // Templates store mappings (admin)
              "templates/catalog": ensureArray(store.catalogList),
              "templates/templateItemById": (id) =>
                ensureValue(store.templateItemById(id)),
              "templates/templateItemByTargetId": (targetId) =>
                ensureValue(store.templateItemByTargetId(targetId)),
              "templates/editorChanges": ensureValue(store.editorChanges, {}),
              "templates/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // TemplateCollections store mappings (admin)
              "templateCollections/templateCollections": ensureArray(
                store.templateCollectionsList,
              ),
              "templateCollections/templateCollectionById": (id) =>
                ensureValue(store.templateCollectionById(id)),
              "templateCollections/editorChanges": ensureValue(
                store.editorChanges,
                {},
              ),
              "templateCollections/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // Organisations store mappings (admin)
              "organisations/organisations": ensureArray(
                store.organisationsList,
              ),
              "organisations/organisationById": (id) =>
                ensureValue(store.organisationById(id)),
              "organisations/editorChanges": ensureValue(
                store.editorChanges,
                {},
              ),
              "organisations/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // Users store mappings (admin)
              "users/users": ensureArray(store.usersList),
              "users/userById": (userId) => ensureValue(store.userById(userId)),
              "users/usersInOrganisation": (orgId) =>
                ensureArray(store.usersInOrganisation(orgId)),
              "users/editorChanges": ensureValue(store.editorChanges, {}),
              "users/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // Vessels store mappings (admin)
              "vessels/vessels": ensureArray(store.vesselsList),
              "vessels/vesselsInOrganisation": (orgId) =>
                ensureArray(store.vesselsInOrganisation(orgId)),
              "vessels/editorChanges": ensureValue(store.editorChanges, {}),
              "vessels/editorHasChanges": ensureValue(
                store.editorHasChanges,
                false,
              ),

              // Features store mappings (admin)
              "features/features": ensureArray(store.featuresList),

              // Roles store mappings (admin)
              "roles/roles": ensureArray(store.rolesList),
            };

            // Check if we have a specific mapping for this getter
            if (getterMappings[mappingKey]) {
              return getterMappings[mappingKey];
            }

            // Fallback: try to find property on store directly
            try {
              if (typeof store[getterName] === "function") {
                return store[getterName];
              } else if (getterName in store) {
                const value = store[getterName];
                return value;
              }
            } catch (error) {
              console.warn(
                `Error accessing store property ${storeName}.${getterName}:`,
                error,
              );
              return undefined;
            }

            console.warn(
              `Getter "${getterName}" not found in store "${storeName}"`,
            );
            return undefined;
          },
        },
      ),

      state: new Proxy(
        {},
        {
          get(target, prop) {
            if (prop === "user") {
              return app.config.globalProperties.$auth?.state?.user;
            }

            const store = getStore(prop);
            if (!store) {
              return undefined;
            }

            return store.$state;
          },
        },
      ),

      dispatch(action, payload) {
        const [storeName, actionName] = action.split("/");
        const store = getStore(storeName);

        if (!store) {
          console.warn(`Store "${storeName}" not found for dispatch`);
          return Promise.resolve();
        }

        try {
          if (typeof store[actionName] === "function") {
            return store[actionName](payload);
          }
        } catch (error) {
          console.warn(`Error dispatching ${action}:`, error);
          return Promise.reject(error);
        }

        console.warn(
          `Action "${actionName}" not found in store "${storeName}"`,
        );
        return Promise.resolve();
      },

      commit(mutation, payload) {
        const [storeName, mutationName] = mutation.split("/");
        const store = getStore(storeName);

        if (!store) {
          console.warn(`Store "${storeName}" not found for commit`);
          return;
        }

        try {
          // Handle common mutation patterns from ModuleBase
          if (
            mutationName === "set" &&
            Array.isArray(payload) &&
            payload.length === 2
          ) {
            const [key, value] = payload;
            if (typeof store.set === "function") {
              store.set(key, value);
              return;
            }
          }

          // Handle other mutations
          if (typeof store[mutationName] === "function") {
            store[mutationName](payload);
            return;
          }
        } catch (error) {
          console.warn(`Error committing ${mutation}:`, error);
          return;
        }

        console.warn(
          `Mutation "${mutationName}" not found in store "${storeName}"`,
        );
      },
    };

    // Add $store to component instances
    app.config.globalProperties.$store = $store;
  },
};
